import React, { useState, useEffect, useRef } from 'react';
import { Menu, Search, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { avatar } from '../assets/assets';
import { useQuery } from '@tanstack/react-query';
import { getCategories } from '../services/category.service';
import { getGigs } from '../services/gig.service';

const EMPTY_ARRAY = [];

const Header = () => {
  const { accessToken, user } = useAuthStore();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isExploreOpen, setIsExploreOpen] = useState(false);
  const [isHeaderFixed, setIsHeaderFixed] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [searchSuggestions, setSearchSuggestions] = useState(EMPTY_ARRAY);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const navigate = useNavigate();

  const searchInputRef = useRef(null);
  const searchSuggestionsRef = useRef(null);
  const exploreBtnRef = useRef(null);
  const exploreDropdownRef = useRef(null);

  // Fetch Categories with caching
  const { data: categoriesData } = useQuery({
    queryKey: ['categories'],
    queryFn: getCategories,
    staleTime: 24 * 60 * 60 * 1000, // 24 hours
  });

  const categories = categoriesData?.categories || EMPTY_ARRAY;

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Fetch Gigs for search suggestions
  const { data: searchGigsData } = useQuery({
    queryKey: ['searchGigs', debouncedSearchQuery],
    queryFn: () => getGigs({ search: debouncedSearchQuery, limit: 5 }),
    enabled: debouncedSearchQuery.length > 2,
    keepPreviousData: true,
  });

  const searchGigs = searchGigsData?.gigs || EMPTY_ARRAY;

  // Handle search suggestions logic
  useEffect(() => {
    if (debouncedSearchQuery.length > 1) {
      const query = debouncedSearchQuery.toLowerCase();

      // Search in categories (client-side filtering of cached data)
      const categoryMatches = categories
        .filter(category => category.name.toLowerCase().includes(query))
        .map(category => ({
          id: category._id,
          text: category.name,
          type: 'category',
          url: `/category/${category.slug || category._id}` // Assuming slug exists or fallback to ID
        }));

      // Map API gig results
      const gigMatches = searchGigs.map(gig => ({
        id: gig._id,
        text: gig.title,
        type: 'gig',
        url: `/gigs/${gig.slug}`,
        category: gig.category?.name || 'Service'
      }));

      // Combine results
      const results = [...categoryMatches, ...gigMatches];

      // Limit to 6 results
      const limitedResults = results.slice(0, 6);
      setSearchSuggestions(limitedResults);
      setShowSuggestions(limitedResults.length > 0);
    } else {
      if (searchSuggestions.length > 0) setSearchSuggestions(EMPTY_ARRAY);
      if (showSuggestions) setShowSuggestions(false);
    }
  }, [debouncedSearchQuery, categories, searchGigs]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    // Initial check
    handleResize();

    // Add event listener
    window.addEventListener('resize', handleResize);

    // Clean up
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Handle scroll to make header fixed
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 100) {
        setIsHeaderFixed(true);
      } else {
        setIsHeaderFixed(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Close menu if screen size changes from mobile to desktop
  useEffect(() => {
    if (!isMobile) {
      setIsMenuOpen(false);
    }
  }, [isMobile]);

  // Focus search input when opened
  useEffect(() => {
    if (isSearchOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isSearchOpen]);

  // Close search and suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isSearchOpen &&
        !event.target.closest('.search-container') &&
        !event.target.closest('.search-suggestions')) {
        setShowSuggestions(false);
        // Option to close search entirely or just suggestions
        // setIsSearchOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isSearchOpen]);

  // Close explore dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        !isMobile &&
        isExploreOpen &&
        exploreDropdownRef.current &&
        !exploreDropdownRef.current.contains(event.target) &&
        !exploreBtnRef.current.contains(event.target)
      ) {
        setIsExploreOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isExploreOpen, isMobile]);

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const resetSearch = () => {
    setSearchQuery('');
    setDebouncedSearchQuery('');
    setShowSuggestions(false);
  };
  
  const handleEnterKey = (e) => {
    if (e.key === 'Enter' && searchQuery.trim()) {
      setShowSuggestions(false);
      setIsSearchOpen(false);
      navigate(`/category/all-services?search=${encodeURIComponent(searchQuery.trim())}`);
      resetSearch();
    }
  };

  // Mobile menu overlay
  if (isMenuOpen) {
    return (
      <div className="fixed inset-0 bg-white z-50">
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <button
            className="text-gray-600"
            onClick={() => setIsMenuOpen(false)}
          >
            <X size={24} />
          </button>

          <h1 className="text-xl font-semibold text-[#174568]">
            Hello<span className="text-[#7295B8]">cer</span>
          </h1>

          <div className="search-container relative">
            {isSearchOpen ? (
              <div className="flex items-center bg-gray-100 rounded-full pl-4 pr-2 py-1">
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder="Search..."
                  className="bg-transparent outline-none w-32 hidden "
                  value={searchQuery}
                  onChange={handleSearchChange}
                  onKeyDown={handleEnterKey}
                />
                <button
                  onClick={resetSearch}
                  className="ml-2 text-gray-600"
                >
                  <X size={16} />
                </button>
              </div>
            ) : (
              <button
                className="text-gray-600 hidden"
                onClick={() => setIsSearchOpen(true)}
              >
                <Search size={20} />
              </button>
            )}

            {/* Mobile search suggestions */}
            {isSearchOpen && showSuggestions && (
              <div
                className="search-suggestions absolute top-full left-0 mt-1 w-64 bg-white shadow-lg rounded-lg z-50 overflow-hidden"
                ref={searchSuggestionsRef}
              >
                <ul className="py-1">
                  {searchSuggestions.map((suggestion) => (
                    <li key={`${suggestion.type}-${suggestion.id}`}>
                      <a
                        to={suggestion.url}
                        className="flex flex-col px-4 py-2 hover:bg-gray-100"
                        onClick={() => {
                          resetSearch();
                          setIsSearchOpen(false);
                        }}
                      >
                        <span className="font-medium">{suggestion.text}</span>
                        {suggestion.type === 'gig' && (
                          <span className="text-xs text-gray-500">in {suggestion.category}</span>
                        )}
                        <span className="text-xs text-blue-500">
                          {suggestion.type === 'category' ? 'Category' : 'Gig'}
                        </span>
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>

        <nav className="p-4">
          <ul className="space-y-6">
            <li>
              <a href="/how-it-works" className="block text-gray-700 py-2" onClick={() => setIsMenuOpen(false)}>How it works?</a>
            </li>
            <li>
              <div className="flex items-center justify-between" onClick={() => setIsExploreOpen(!isExploreOpen)}>
                <a href="#" className="block text-gray-700 py-2">Explore</a>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className={`h-6 w-6 text-gray-400 transition-transform ${isExploreOpen ? 'rotate-180' : ''}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
              {/* Mobile dropdown - pushes content down */}
              {isExploreOpen && (
                <div className="mt-2 pl-4 border-l border-gray-200">
                  {categories.map((category) => (
                    <a
                      key={category._id}
                      href={`/category/${category.slug || category._id}`}
                      className="block py-2 text-gray-600 hover:text-[#174568]"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      {category.name}
                    </a>
                  ))}
                  {categories.length === 0 && (
                     <span className="block py-2 text-gray-400 text-sm">Loading categories...</span>
                  )}
                </div>
              )}
            </li>
            <li>
              <a href="/hire-talents" className="block text-gray-700 py-2">Hire talents</a>
            </li>

               <li>
              <a href="/clients/signin" className="block text-gray-700 py-2">Sign up/login</a>
            </li>

          </ul>
        </nav>
      </div>
    );
  }

  return (
    <header className={`flex items-center justify-between px-4 py-3 bg-white border-b border-gray-200 w-full z-40 ${isHeaderFixed ? 'fixed top-0 left-0 right-0 shadow-md' : ''}`}>
      {/* Left section - conditionally show menu icon on mobile */}
      <div className="flex items-center">
        {isMobile && (
          <button
            className="text-gray-600 mr-3"
            onClick={() => setIsMenuOpen(true)}
          >
            <Menu size={24} />
          </button>
        )}

        <a href='/' className="text-xl font-semibold text-[#174568]">
          Hello<span className="text-[#7295B8]">cer</span>
        </a>
      </div>

      {/* Middle section - only show on desktop */}
      {!isMobile && (
        <div className="flex items-center space-x-6 ml-10">
          <a href="/how-it-works" className="text-gray-700 hover:text-blue-900">How it works?</a>
          <div className="relative">
            <button
              ref={exploreBtnRef}
              className="flex items-center text-gray-700 hover:text-blue-900 focus:outline-none"
              onClick={() => setIsExploreOpen(!isExploreOpen)}
            >
              Explore
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className={`h-4 w-4 ml-1 transition-transform duration-200 ${isExploreOpen ? 'rotate-180' : ''}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {/* Desktop dropdown - absolute positioning to float over content */}
            {isExploreOpen && (
              <div
                ref={exploreDropdownRef}
                className="absolute top-full left-0 mt-2 py-3 bg-white border border-gray-200 rounded-lg shadow-lg transform origin-top transition-all duration-200 w-[max-content] min-w-[200px] z-50 max-h-96 overflow-y-auto"
              >
                {categories.length > 0 ? (
                  categories.map((category) => (
                    <a
                      key={category._id}
                      href={`/category/${category.slug || category._id}`}
                      className="block px-4 py-2 text-gray-700 hover:bg-gray-100 text-nowrap"
                      onClick={() => setIsExploreOpen(false)}
                    >
                      {category.name}
                    </a>
                  ))
                ) : (
                  <div className="px-4 py-2 text-gray-500 text-sm">Loading...</div>
                )}
              </div>
            )}
          </div>
           <a href="/hire-talents" className="text-gray-700 hover:text-blue-900">Hire talents</a>
        </div>
      )}

      {/* Right section with animated search */}
      <div className="flex items-center">
        <div className="search-container relative mr-2">
          <div className={`flex items-center transition-all duration-300 ${isSearchOpen ? 'w-48' : 'w-8'} overflow-hidden rounded-full bg-gray-100`}>
            {isSearchOpen && (
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Search..."
                className="ml-3 pl-1 py-1 bg-transparent outline-none w-full"
                value={searchQuery}
                onChange={handleSearchChange}
                onKeyDown={handleEnterKey}
              />
            )}
            <button
              className={`${isSearchOpen ? 'ml-2' : 'm-auto'} p-1 text-gray-600 hover:text-gray-900`}
              onClick={() => {
                if (isSearchOpen) {
                  resetSearch();
                }
                setIsSearchOpen(!isSearchOpen);
              }}
            >
              {isSearchOpen ? <X size={16} /> : <Search size={20} />}
            </button>
          </div>

          {/* Desktop search suggestions */}
          {isSearchOpen && showSuggestions && (
            <div
              className="search-suggestions absolute top-full right-0 mt-1 w-64 bg-white shadow-lg rounded-lg z-50 overflow-hidden"
              ref={searchSuggestionsRef}
            >
              <ul className="py-1">
                {searchSuggestions.map((suggestion) => (
                  <li key={`${suggestion.type}-${suggestion.id}`}>
                    <a
                      href={suggestion.url}
                      className="flex flex-col px-4 py-2 hover:bg-gray-100"
                      onClick={() => {
                        resetSearch();
                        setIsSearchOpen(false);
                      }}
                    >
                      <span className="font-medium text-[15px]">{suggestion.text}</span>

                      <div className="flex items-center gap-2">
                        {suggestion.type === 'gig' && (
                          <span className="text-[13px] text-gray-500">in {suggestion.category}</span>
                        )}
                        <span className="text-[12px] text-blue-500">
                          {suggestion.type === 'category' ? 'Category' : 'Gig'}
                        </span>
                      </div>
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {
          !accessToken ? (
            <a href={'/clients/signup'} className="hidden md:flex bg-[#174568] text-white px-4 py-2 rounded-full text-sm font-medium hover:bg-gray-800">
              Sign up/login
            </a>
          ) : (
            <a href={'/clients/dashboard'} className="w-7 h-7 rounded-full overflow-hidden">
              <img src={user?.profilePicture || avatar} alt='avatar' className='w-full h-full'/>
            </a>
          )
        }

      </div>
    </header>
  );
};

export default Header;